import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { SocketKey } from '@/Config/api/socketKey';
import { Event } from '@/Domain/api/Conversation/dto/Event.dto';
import { DeliveryStatus, Message } from '@/Domain/api/Conversation/dto/Message.dto';
import { Paginated } from '@/Domain/api/Shared/dto/Paginated.dto';
import { useSocket } from '@/Domain/components/SocketProvider';
import { EventTypeEnum } from '@/Domain/enum/EventType.enum';

type ConversationSocket<T = Event> = { event: T; conversation_id: string };

type DeliveryStatusSocket = {
  conversation_id: string;
  message_id: number;
  status: DeliveryStatus;
  error_message: string | null;
};

export const useConversationEvent = (id: string) => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  useEffect(() => {
    socket.on(
      SocketKey.conversation.eventCreated,
      ({ event, conversation_id: conversationId }: ConversationSocket) => {
        if (conversationId === id) {
          queryClient.setQueryData(
            ['conversations', id, 'events'],
            (oldData: InfiniteData<Paginated<Event[]>>) => {
              if (oldData !== undefined) {
                return {
                  ...oldData,
                  pages: oldData.pages.map((page: Paginated<Event[]>) => {
                    const { items } = page;
                    if (
                      event.event_type === EventTypeEnum.LOG ||
                      event.event_type === EventTypeEnum.NOTE ||
                      event.event_type === EventTypeEnum.MESSAGE ||
                      event.event_type === EventTypeEnum.LINK_CLICKED ||
                      event.event_type === EventTypeEnum.ASSISTED_ORDER
                    ) {
                      return {
                        ...page,
                        items: [event, ...items],
                      };
                    }
                    return page;
                  }),
                };
              }
              return undefined;
            },
          );
        } else {
          queryClient
            .invalidateQueries({ queryKey: ['conversations', conversationId, 'events'] })
            .catch(() => null);
        }
      },
    );

    socket.on(
      SocketKey.conversation.translated,
      ({ event, conversation_id: conversationId }: ConversationSocket<Message>) => {
        if (conversationId === id) {
          queryClient.setQueryData(
            ['conversations', id, 'events'],
            (oldData: InfiniteData<Paginated<Event[]>>) => {
              if (!oldData) return undefined;

              return {
                ...oldData,
                pages: oldData.pages.map((page) => {
                  const { items } = page;
                  const itemIndex = items.findIndex(
                    (item) =>
                      (item.event_type === EventTypeEnum.MESSAGE && item.data?.id) === event.id,
                  );

                  if (itemIndex !== -1) {
                    const updatedItems = [...items];

                    if (
                      updatedItems[itemIndex].event_type === EventTypeEnum.MESSAGE &&
                      !updatedItems[itemIndex].data.content_translated
                    ) {
                      updatedItems[itemIndex] = { ...updatedItems[itemIndex], data: event };
                      return { ...page, items: updatedItems };
                    }
                  }
                  return page;
                }),
              };
            },
          );
        }
      },
    );
    socket.on(SocketKey.conversation.deliveryStatusUpdated, (data: DeliveryStatusSocket) => {
      if (data.conversation_id !== id) return;

      queryClient.setQueryData(
        ['conversations', id, 'events'],
        (oldData: InfiniteData<Paginated<Event[]>>) => {
          if (!oldData) return undefined;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              items: page.items.map((item) => {
                if (
                  item.event_type === EventTypeEnum.MESSAGE &&
                  String(item.data.id) === String(data.message_id)
                ) {
                  return {
                    ...item,
                    data: {
                      ...item.data,
                      delivery_status: data.status,
                      delivery_error: data.error_message,
                    },
                  };
                }
                return item;
              }),
            })),
          };
        },
      );
    });

    return () => {
      socket.off(SocketKey.conversation.eventCreated);
      socket.off(SocketKey.conversation.translated);
      socket.off(SocketKey.conversation.deliveryStatusUpdated);
    };
  }, [id, socket]);
};
